// pages/PersonalizationPage.js
import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import UserService from '../services/UserService'
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc
} from 'firebase/firestore'
import { app } from '../firebase'
import toast from 'react-hot-toast'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import './PersonalizationPage.css'

function PersonalizationPage () {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [botName, setBotName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [companyId, setCompanyId] = useState(null)

  const db = getFirestore(app)

  useEffect(() => {
    if (!user) {
      setLoading(false)
      setError('Bitte melden Sie sich an')
      return
    }

    const loadSettings = async () => {
      try {
        // Get user's companyId
        const userCompanyId = await UserService.getUserCompanyId(user.uid)
        setCompanyId(userCompanyId)

        // Load company data
        const companyRef = doc(db, 'companies', userCompanyId)
        const companyDoc = await getDoc(companyRef)

        if (companyDoc.exists()) {
          const companyData = companyDoc.data()
          setCompanyName(companyData.name || '')

          // Load bot name from settings, default to "Service-Assistent"
          const currentBotName = companyData.settings?.botName || 'Service-Assistent'
          setBotName(currentBotName)
        }

        setLoading(false)
      } catch (err) {
        console.error('Error loading settings:', err)
        setError('Fehler beim Laden der Einstellungen')
        setLoading(false)
      }
    }

    loadSettings()
  }, [db, user])

  const handleSave = async (e) => {
    e.preventDefault()

    // Validation
    if (!botName.trim()) {
      toast.error('Bitte geben Sie einen Bot-Namen ein')
      return
    }

    if (botName.trim().length < 2) {
      toast.error('Bot-Name muss mindestens 2 Zeichen lang sein')
      return
    }

    if (botName.trim().length > 50) {
      toast.error('Bot-Name darf maximal 50 Zeichen lang sein')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const companyRef = doc(db, 'companies', companyId)

      // Update settings.botName
      await updateDoc(companyRef, {
        'settings.botName': botName.trim()
      })

      toast.success('Bot-Name erfolgreich gespeichert!')
    } catch (err) {
      console.error('Error saving bot name:', err)
      setError('Fehler beim Speichern des Bot-Namens')
      toast.error('Fehler beim Speichern')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setBotName('Service-Assistent')
  }

  if (loading) {
    return (
      <div className='personalization-page'>
        <div className='loading'>Lädt Einstellungen...</div>
      </div>
    )
  }

  return (
    <div className='personalization-page'>
      <div className='page-header'>
        <h1>Personalisierung</h1>
        <p className='page-subtitle'>
          Passen Sie den Namen des Chatbots für Ihr Unternehmen an
        </p>
      </div>

      {error && (
        <div className='error-message'>
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className='settings-container'>
        <div className='settings-card'>
          <h2>Chatbot-Name</h2>
          <p className='card-description'>
            Legen Sie fest, wie der Chatbot in Gesprächen und in der Suchansicht
            angezeigt wird.
          </p>

          <form onSubmit={handleSave} className='settings-form'>
            <div className='form-group'>
              <label htmlFor='botName'>Name des Chatbots</label>
              <input
                type='text'
                id='botName'
                value={botName}
                onChange={(e) => setBotName(e.target.value)}
                placeholder='z.B. Service-Assistent, TechBot, Helfer'
                disabled={saving}
                maxLength={50}
              />
              <span className='char-count'>
                {botName.length} / 50 Zeichen
              </span>
            </div>

            <div className='form-actions'>
              <button
                type='button'
                className='btn-secondary'
                onClick={handleReset}
                disabled={saving}
              >
                Zurücksetzen
              </button>
              <button
                type='submit'
                className='btn-primary'
                disabled={saving || !botName.trim()}
              >
                {saving ? 'Speichere...' : 'Speichern'}
              </button>
            </div>
          </form>
        </div>

        <div className='preview-card'>
          <h2>Vorschau</h2>
          <p className='card-description'>
            So wird der Bot-Name in der Anwendung angezeigt:
          </p>

          <div className='preview-section'>
            <h3>Chat-Nachricht:</h3>
            <div className='preview-message bot-message'>
              <div className='message-header'>
                <SmartToyIcon
                  fontSize='small'
                  style={{ verticalAlign: 'middle', marginRight: '0.35rem' }}
                />
                <span className='bot-name'>{botName || 'Service-Assistent'}</span>
              </div>
              <div className='message-content'>
                Wie kann ich Ihnen heute helfen?
              </div>
            </div>
          </div>

          <div className='preview-section'>
            <h3>Suchseite Header:</h3>
            <div className='preview-header'>
              <h2>
                {companyName && <span>{companyName} </span>}
                {botName || 'Service-Assistent'}
              </h2>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PersonalizationPage
